/* eslint-disable */
import { MEMORY_CONFIG } from '@/constants/memoryConfig'

export class ChocoboLazyLoader {
  static detailCache = new Map()
  static compressedChocobos = new Map()
  static lastAccessTime = new Map()
  static CACHE_SIZE = 100 // Keep full details for 100 most recently accessed chocobos
  static CACHE_TTL = 300000 // 5 minutes in milliseconds

  static compressChocobo(chocobo) {
    // Create a minimal representation for background chocobos
    return {
      id: chocobo.id,
      name: chocobo.name,
      ownerId: null, // Will be set by owner lookup
      level: chocobo.level,
      speed: chocobo.speed,
      stamina: chocobo.stamina,
      color: chocobo.color,
      behaviorId: chocobo.behaviorId,
      // Store only essential race data
      recentForm: chocobo.recentForm || [],
      lastRaceDay: chocobo.raceHistory?.length > 0 
        ? chocobo.raceHistory[chocobo.raceHistory.length - 1].day 
        : 0,
      totalRaces: chocobo.raceHistory?.length || 0,
      wins: chocobo.raceHistory?.filter(r => r.position === 1).length || 0
    }
  }

  static hydrateChocobo(compressed, fullData) {
    // Restore full chocobo object from compressed + stored data
    const hydrated = { ...fullData }
    
    // Update any fields that might have changed in compressed version
    hydrated.level = compressed.level
    hydrated.recentForm = compressed.recentForm
    hydrated.speed = compressed.speed
    hydrated.stamina = compressed.stamina

    return hydrated
  }

  static getChocobo(chocoboId, gameStore) {
    const now = Date.now()
    
    // Check if we have it in detail cache
    if (this.detailCache.has(chocoboId)) {
      const cached = this.detailCache.get(chocoboId)
      this.lastAccessTime.set(chocoboId, now)
      
      // Check if cache is still valid
      if (now - cached.timestamp < this.CACHE_TTL) {
        return cached.chocobo
      }
    }

    // Look for full chocobo in game store
    const fullChocobo = gameStore.chocobos.find(c => c.id === chocoboId)
    if (fullChocobo) {
      this.cacheChocobo(fullChocobo)
      return fullChocobo
    }

    // Check compressed storage
    if (this.compressedChocobos.has(chocoboId)) {
      const compressed = this.compressedChocobos.get(chocoboId)
      // In a real implementation, we'd restore from persistent storage
      // For now, return a simplified version
      return this.createMinimalChocobo(compressed)
    }

    return null
  }

  static cacheChocobo(chocobo) {
    const now = Date.now()
    
    // Add to detail cache
    this.detailCache.set(chocobo.id, {
      chocobo: chocobo,
      timestamp: now
    })
    this.lastAccessTime.set(chocobo.id, now)

    // Enforce cache size limit
    if (this.detailCache.size > this.CACHE_SIZE) {
      this.evictOldestEntries()
    }
  }

  static evictOldestEntries() {
    // Sort by last access time
    const sorted = Array.from(this.lastAccessTime.entries())
      .sort((a, b) => a[1] - b[1])

    // Remove oldest entries
    const toRemove = sorted.slice(0, this.detailCache.size - this.CACHE_SIZE)
    toRemove.forEach(([chocoboId]) => {
      const cached = this.detailCache.get(chocoboId)
      if (cached) {
        // Compress before removing from cache
        this.compressedChocobos.set(chocoboId, this.compressChocobo(cached.chocobo))
      }
      this.detailCache.delete(chocoboId)
      this.lastAccessTime.delete(chocoboId)
    })
  }

  static createMinimalChocobo(compressed) {
    // Create a minimal chocobo object for display/racing
    // This won't have full functionality but enough for background races
    return {
      id: compressed.id,
      name: compressed.name,
      level: compressed.level,
      speed: compressed.speed,
      stamina: compressed.stamina,
      color: compressed.color,
      behaviorId: compressed.behaviorId,
      recentForm: compressed.recentForm,
      // Minimal methods
      recordRaceResult: function() {
        // No-op for compressed chocobos
      },
      gainExperience: function() {
        // No-op for compressed chocobos
      }
    }
  }

  static preloadChocobos(chocoboIds, gameStore) {
    // Batch load multiple chocobos efficiently
    const toLoad = chocoboIds.filter(id => !this.detailCache.has(id))
    
    toLoad.forEach(id => {
      const chocobo = gameStore.chocobos.find(c => c.id === id)
      if (chocobo) {
        this.cacheChocobo(chocobo)
      }
    })
  }

  static compressInactiveChocobos(gameStore) {
    const now = Date.now()
    const inactiveThreshold = now - (MEMORY_CONFIG.compressionThreshold * 60000) // Convert minutes to ms

    gameStore.chocobos.forEach(chocobo => {
      const lastAccess = this.lastAccessTime.get(chocobo.id) || 0
      
      // Compress chocobos that haven't been accessed recently
      if (lastAccess < inactiveThreshold) {
        // Don't compress player's chocobos
        const owner = gameStore.owners.find(o => o.chocoboIds.includes(chocobo.id))
        if (!owner?.isPlayer) {
          this.compressedChocobos.set(chocobo.id, this.compressChocobo(chocobo))
          
          // Remove from detail cache to save memory
          this.detailCache.delete(chocobo.id)
        }
      }
    })
  }

  static getChocoboStats(chocoboId) {
    // Quick stats lookup without full hydration
    if (this.compressedChocobos.has(chocoboId)) {
      const compressed = this.compressedChocobos.get(chocoboId)
      return {
        level: compressed.level,
        speed: compressed.speed,
        stamina: compressed.stamina,
        totalRaces: compressed.totalRaces,
        wins: compressed.wins
      }
    }

    const cached = this.detailCache.get(chocoboId)
    if (cached) {
      const chocobo = cached.chocobo
      return {
        level: chocobo.level,
        speed: chocobo.speed,
        stamina: chocobo.stamina,
        totalRaces: chocobo.raceHistory?.length || 0,
        wins: chocobo.raceHistory?.filter(r => r.position === 1).length || 0
      }
    }

    return null
  }

  static clearCache() {
    this.detailCache.clear()
    this.compressedChocobos.clear()
    this.lastAccessTime.clear()
  }
}